import React, { useState, useRef, useEffect } from "react";
import moment from "moment";

// Date header component for Space messages
type SpaceDateHeaderProps = {
    date: string | Date;
    className?: string;
    onJumpToDate?: (option: string) => void;
};

type DateJumpOption = {
    label: string;
    value: string;
    action: () => void;
} & React.HTMLAttributes<HTMLDivElement>;

export default function SpaceDateHeader(props: SpaceDateHeaderProps) {
    const { date, className, onJumpToDate, ...rest } = props;

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const formatDate = (date: string | Date) => {
        const momentDate = moment(date);
        const today = moment();
        const yesterday = moment().subtract(1, "day");

        if (momentDate.isSame(today, "day")) {
            return "Today";
        } else if (momentDate.isSame(yesterday, "day")) {
            return "Yesterday";
        } else {
            return momentDate.format("dddd, MMMM DD");
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const jumpOptions: DateJumpOption[] = [
        {
            label: "Today",
            value: "today",
            action: () => onJumpToDate?.("today"),
        },
        {
            label: "Yesterday",
            value: "yesterday",
            action: () => onJumpToDate?.("yesterday"),
        },
        {
            label: "Last week",
            value: "last-week",
            action: () => onJumpToDate?.("last-week"),
        },
        {
            label: "Last month",
            value: "last-month",
            action: () => onJumpToDate?.("last-month"),
        },
        {
            label: "The very beginning",
            value: "beginning",
            action: () => onJumpToDate?.("beginning"),
        },
        {
            label: "Jump to a specific date",
            value: "specific",
            action: () => onJumpToDate?.("specific"),
        },
    ];

    const handleOptionClick = (option: DateJumpOption) => {
        option.action();
        setIsDropdownOpen(false);
    };

    return (
        <div
            className={`flex items-center justify-center pt-12 pb-8 ${className || ""}`}
            data-date-header={moment(date).format("YYYY-MM-DD")}
            {...rest}
        >
            {/* Left divider line */}
            <div className="flex-1 h-px bg-gray-200"></div>

            {/* Date pill with dropdown */}
            <div>
                <div className="relative " ref={dropdownRef}>
                    <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-shadow duration-200 flex items-center gap-2"
                    >
                        <span className="text-sm font-medium text-gray-600">
                            {formatDate(date)}
                        </span>
                        <i
                            className={`ri-arrow-down-s-line text-gray-500 text-sm transition-transform duration-200 ${
                                isDropdownOpen ? "rotate-180" : ""
                            }`}
                        ></i>
                    </button>

                    {/* Dropdown menu */}
                    {isDropdownOpen && (
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                            <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                                Jump to...
                            </div>
                            {jumpOptions.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => handleOptionClick(option)}
                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Right divider line */}
            <div className="flex-1 h-px bg-gray-200"></div>
        </div>
    );
}
